import { Component, type ReactNode } from 'react';
import { avisoDeFallo } from './avisoDeFallo';

/**
 * Límite de errores del canvas del avatar.
 *
 * Atrapa cualquier falla de dentro del `<Canvas>` —`.glb` inexistente o
 * corrupto, asset que no cumple el estándar (`AssetInvalidoError`), WebGL
 * no disponible— para que no tumbe la pantalla que lo contiene. R3F
 * reenvía al árbol de React los errores de dentro del canvas, así que un
 * límite de React alcanza; verificado el 2026-09-22 con un asset de
 * Character Creator, cuyo error llegó hasta el límite por defecto de
 * React Router.
 *
 * Lo usan los dos consumidores del canvas con distinto `fallback`: la
 * entrevista cae al marcador (D-9) y `/dev/avatar` muestra el
 * diagnóstico. **Un solo límite, dos presentaciones** — duplicarlo sería
 * garantizar que las dos se separen.
 */

export interface LimiteDeErroresProps {
  /**
   * Identidad de lo que se vigila: la URL del `.glb`. Al cambiar, el
   * límite se rearma y vuelve a renderizar los hijos.
   *
   * Sin esto, un asset fallido deja la pantalla en su fallback para
   * siempre. Hoy en la entrevista no muerde (una sesión, un avatar), pero
   * sí en cuanto una página deja elegir el asset.
   */
  url: string;
  /** `avatar_id` del escenario, para el aviso de consola. */
  avatarId?: string;
  /** Aviso al contenedor, que suele tener su propio estado de fallo. */
  onError?: (error: unknown) => void;
  /** Qué mostrar en lugar de los hijos. Por defecto, nada. */
  fallback?: (error: unknown) => ReactNode;
  children: ReactNode;
}

interface Estado {
  fallo: boolean;
  error: unknown;
}

const SIN_FALLO: Estado = { fallo: false, error: null };

export class LimiteDeErrores extends Component<LimiteDeErroresProps, Estado> {
  override state: Estado = SIN_FALLO;

  static getDerivedStateFromError(error: unknown): Estado {
    return { fallo: true, error };
  }

  override componentDidCatch(error: unknown): void {
    console.warn(avisoDeFallo(this.props.url, error, this.props.avatarId), error);
    this.props.onError?.(error);
  }

  override componentDidUpdate(anterior: LimiteDeErroresProps): void {
    if (anterior.url !== this.props.url && this.state.fallo) this.setState(SIN_FALLO);
  }

  override render(): ReactNode {
    if (!this.state.fallo) return this.props.children;
    return this.props.fallback?.(this.state.error) ?? null;
  }
}
